import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../../lib/api';
import { Boxes, CheckCircle2, ArrowLeftRight, ShieldAlert, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';

const KPI = ({ icon: Icon, label, value, tone = 'default', testId }) => {
  const tones = { default: 'bg-white border-slate-200', amber: 'bg-amber-50 border-amber-200', red: 'bg-red-50 border-red-200', green: 'bg-emerald-50 border-emerald-200' };
  return (
    <div className={`rounded-sm border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500"><Icon size={13} /> {label}</div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#1F7A6E]">{value}</div>
    </div>
  );
};

export default function AssetDashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet('/hr/assets/dashboard').then(setD).catch(() => setD({})); }, []);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="asset-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">HR · Admin</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Asset Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Inventory health · Warranty · Overdue returns.</p>
        </div>
        <button onClick={() => nav('/app/hr/assets/register')} data-testid="open-register-btn" className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Boxes size={14} /> Asset Register</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Boxes} label="Total Assets" value={d.total || 0} testId="kpi-total" />
        <KPI icon={CheckCircle2} label="Available" value={d.available || 0} tone="green" testId="kpi-available" />
        <KPI icon={ArrowLeftRight} label="Allocated" value={d.allocated || 0} testId="kpi-allocated" />
        <KPI icon={Clock} label="Overdue Returns" value={d.overdue_count || 0} tone={d.overdue_count ? 'red' : 'default'} testId="kpi-overdue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card data-testid="by-category-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">By Category</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(d.by_category || {}).length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">No assets.</div> : (
              <div className="space-y-1.5">
                {Object.entries(d.by_category).map(([c, n]) => (
                  <div key={c} className="flex items-center justify-between text-xs border-b border-slate-100 py-1.5"><span className="text-slate-600">{c}</span><span className="font-display font-bold">{n}</span></div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="warranty-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2"><ShieldAlert size={15} /> Warranty Expiring (60d)</CardTitle></CardHeader>
          <CardContent>
            {(d.warranty_expiring_60d || []).length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">No warranties expiring soon.</div> : (
              <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                {d.warranty_expiring_60d.map((a) => (
                  <div key={a.id} className="py-2 flex items-center justify-between text-xs" data-testid={`warranty-${a.id}`}>
                    <div><span className="font-display font-bold">{a.name}</span> <span className="text-slate-400">· {a.asset_tag}</span></div>
                    <span className="text-amber-700 font-bold">{a.warranty_expiry}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(d.overdue_returns || []).length > 0 && (
        <Card className="border-l-4 border-l-red-500" data-testid="overdue-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-red-600 flex items-center gap-2"><Clock size={15} /> Overdue Returns</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {d.overdue_returns.map((al) => (
                <div key={al.id} className="py-2 flex items-center justify-between text-xs" data-testid={`overdue-${al.id}`}>
                  <div><span className="font-display font-bold">{al.asset_name}</span> <span className="text-slate-400">· {al.asset_tag}</span> → {al.employee_name}</div>
                  <span className="text-red-600 font-bold">due {al.expected_return_date}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
